import { select, selectAll } from "../core/dom";

const HEADER_OFFSET = 90;
const CLOSE_ANIMATION_MS = 240;
const ICON_MORPH_MS = 420;

type LineGeometry = readonly [number, number, number, number];
type BrandLineName = "left" | "right" | "base";

const LOGO_GEOMETRY: Record<BrandLineName, LineGeometry> = {
  left: [5, 21, 12, 3],
  right: [12, 3, 19, 21],
  base: [5, 21, 12, 21],
};

const CLOSE_GEOMETRY: Record<BrandLineName, LineGeometry> = {
  left: [4.5, 4.5, 19.5, 19.5],
  right: [19.5, 4.5, 4.5, 19.5],
  base: [12, 12, 12, 12],
};

const easeOutCubic = (value: number): number => 1 - (1 - value) ** 3;

function readLineGeometry(line: SVGLineElement): LineGeometry {
  return [
    Number(line.getAttribute("x1")),
    Number(line.getAttribute("y1")),
    Number(line.getAttribute("x2")),
    Number(line.getAttribute("y2")),
  ];
}

function writeLineGeometry(line: SVGLineElement, geometry: LineGeometry): void {
  line.setAttribute("x1", geometry[0].toFixed(3));
  line.setAttribute("y1", geometry[1].toFixed(3));
  line.setAttribute("x2", geometry[2].toFixed(3));
  line.setAttribute("y2", geometry[3].toFixed(3));
}

export function initMobileNavigation(): void {
  const checkbox = select<HTMLInputElement>(".menu-checkbox");
  const wrapper = select<HTMLElement>(".header-wrapper");
  const navigation = wrapper ? select<HTMLElement>("nav", wrapper) : null;
  const menuButton = wrapper ? select<HTMLLabelElement>(".menu-button", wrapper) : null;

  if (!checkbox || !wrapper || !navigation || !menuButton) return;

  const brandIcon = select<SVGSVGElement>(".menu-brand-icon", menuButton);
  const brandLeft = brandIcon
    ? select<SVGLineElement>('[data-menu-brand-line="left"]', brandIcon)
    : null;
  const brandRight = brandIcon
    ? select<SVGLineElement>('[data-menu-brand-line="right"]', brandIcon)
    : null;
  const brandBase = brandIcon
    ? select<SVGLineElement>('[data-menu-brand-line="base"]', brandIcon)
    : null;

  if (!brandIcon || !brandLeft || !brandRight || !brandBase) return;

  const brandLines: Record<BrandLineName, SVGLineElement> = {
    left: brandLeft,
    right: brandRight,
    base: brandBase,
  };

  let closeTimer: number | undefined;
  let iconAnimationFrame = 0;

  const morphBrandIcon = (isOpen: boolean, animate = true): void => {

    const target = isOpen ? CLOSE_GEOMETRY : LOGO_GEOMETRY;
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    window.cancelAnimationFrame(iconAnimationFrame);

    if (!animate || reducedMotion) {
      (Object.keys(brandLines) as BrandLineName[]).forEach((name) => {
        writeLineGeometry(brandLines[name], target[name]);
      });
      brandLines.base.style.opacity = isOpen ? "0" : "1";
      return;
    }

    const start: Record<BrandLineName, LineGeometry> = {
      left: readLineGeometry(brandLines.left),
      right: readLineGeometry(brandLines.right),
      base: readLineGeometry(brandLines.base),
    };

    const startBaseOpacity = Number.parseFloat(getComputedStyle(brandLines.base).opacity);
    const fromOpacity = Number.isFinite(startBaseOpacity) ? startBaseOpacity : 1;
    const toOpacity = isOpen ? 0 : 1;
    const startedAt = performance.now();

    const tick = (now: number): void => {
      const elapsed = Math.min((now - startedAt) / ICON_MORPH_MS, 1);
      const progress = easeOutCubic(elapsed);

      (Object.keys(brandLines) as BrandLineName[]).forEach((name) => {
        const from = start[name];
        const to = target[name];
        const current: LineGeometry = [
          from[0] + (to[0] - from[0]) * progress,
          from[1] + (to[1] - from[1]) * progress,
          from[2] + (to[2] - from[2]) * progress,
          from[3] + (to[3] - from[3]) * progress,
        ];

        writeLineGeometry(brandLines[name], current);
      });

      brandLines.base.style.opacity = String(fromOpacity + (toOpacity - fromOpacity) * progress);

      if (elapsed < 1) {
        iconAnimationFrame = window.requestAnimationFrame(tick);
      }
    };

    iconAnimationFrame = window.requestAnimationFrame(tick);
  };

  const syncAccessibilityState = (animateIcon = true): void => {
    menuButton.setAttribute("aria-expanded", String(checkbox.checked));
    navigation.setAttribute(
      "aria-hidden",
      String(!checkbox.checked && window.innerWidth < 930),
    );
    morphBrandIcon(checkbox.checked, animateIcon);
  };

  const closeMenu = (): void => {
    if (!checkbox.checked) return;

    navigation.classList.add("is-closing");
    morphBrandIcon(false);
    window.clearTimeout(closeTimer);

    closeTimer = window.setTimeout(() => {
      checkbox.checked = false;
      navigation.classList.remove("is-closing");
      syncAccessibilityState(false);
    }, CLOSE_ANIMATION_MS);
  };

  if (!navigation.id) navigation.id = "primary-navigation";

  menuButton.setAttribute("role", "button");
  menuButton.setAttribute("aria-controls", navigation.id);
  menuButton.setAttribute("tabindex", "0");

  checkbox.addEventListener("change", () => syncAccessibilityState());

  menuButton.addEventListener("keydown", (event) => {
    if (event.key !== "Enter" && event.key !== " ") return;
    event.preventDefault();
    checkbox.checked = !checkbox.checked;
    syncAccessibilityState();
  });

  for (const link of selectAll<HTMLAnchorElement>('a[href^="#"]')) {
    link.addEventListener("click", (event) => {
      const hash = link.hash;
      const target = hash ? select<HTMLElement>(hash) : null;
      if (!target) return;

      event.preventDefault();
      closeMenu();

      const top = target.getBoundingClientRect().top + window.scrollY - HEADER_OFFSET;
      window.scrollTo({ top, behavior: "smooth" });
      history.replaceState(null, "", hash);
    });
  }

  navigation.addEventListener("click", (event) => {
    if ((event.target as Element).closest("a")) closeMenu();
  });

  document.addEventListener("click", (event) => {
    if (!checkbox.checked || wrapper.contains(event.target as Node)) return;
    closeMenu();
  });

  document.addEventListener("keydown", (event) => {
    if (event.key === "Escape") closeMenu();
  });

  window.addEventListener("resize", () => syncAccessibilityState(false));
  syncAccessibilityState(false);
}
